import React, { useEffect, useState } from "react";
import { signUp } from "../../api/Signup";
import { useUser } from "../../api/UserContext";
import { errorHandler } from "../shared/Utils/ErrorHandler";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import Spinner from "../shared/LoadingSpinner/Spinner";
import SecondaryButton from "../shared/Buttons/SecondaryButton";
import { useLayout } from "../../App";

function SignupPage({ props }) {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    user_type: "",
    token: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [generalError, setGeneralError] = useState("");
  const { updateUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const [urlToken, setUrlToken] = useSearchParams();
  const [imageLoaded, setImageLoaded] = useState(false);
  const { setShowHeader, setShowFooter } = useLayout();

  // Shared className variables
  const inputClassName =
    "w-full px-4 max-[480px]:px-3 border border-[#7C7C7C] rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm max-[480px]:text-xs placeholder-gray-400 text-left h-11 max-[480px]:h-9";
  const labelClassName =
    "block text-sm max-[480px]:text-xs !font-semibold !font-montserrat mb-1 text-left";
  const selectClassName =
    "w-full px-4 max-[480px]:px-3 border border-[#7C7C7C] rounded-full focus:outline-none bg-white text-sm max-[480px]:text-xs appearance-none cursor-pointer text-gray-700 text-left h-11 max-[480px]:h-9";

  // Hide header and footer when component mounts
  useEffect(() => {
    setShowHeader(false);
    setShowFooter(false);

    // Restore header and footer when component unmounts
    return () => {
      setShowHeader(true);
      setShowFooter(true);
    };
  }, [setShowHeader, setShowFooter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (name === "password" || name === "confirmPassword") {
      setPasswordError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");

    if (formData.password !== formData.confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    setPasswordError("");

    try {
      const signUpData = {
        ...formData,
        username: formData.email,
      };
      delete signUpData.confirmPassword;

      const data = await signUp(signUpData);
      if (data && !data.password) {
        updateUser(data);
        navigate(`/profile`);
      }
    } catch (error) {
      const errorMessage = errorHandler(error);
      setGeneralError(errorMessage);
      console.error("Error during signup:", error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    // Preload the background image
    const img = new Image();
    img.src =
      "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/login-signup-pages/sign-up.png";
    img.onload = () => {
      setImageLoaded(true);
    };
  }, []);

  useEffect(() => {
    if (urlToken.get("token")) {
      setFormData((prevState) => ({
        ...prevState,
        token: urlToken.get("token"),
        user_type: "nonprofit_member",
      }));
    }
  }, []);

  // Add more media query breakpoints for better responsiveness
  const matches950px = useMediaQuery("(max-width: 950px)");
  const matches768px = useMediaQuery("(max-width: 768px)");
  const matches540px = useMediaQuery("(max-width: 540px)");

  return (
    <>
      {imageLoaded ? (
        <div className="flex flex-row max-[1200px]:p-1 h-screen max-[1200px]:flex-col max-[1200px]:gap-4 justify-start items-center overflow-hidden">
          {/* Form Section Container - Left Side (~55%) */}
          <div className="w-[55%] max-[1200px]:w-full h-screen flex items-center justify-center">
            {/* Form Section - Left Side */}
            <div className="w-full max-w-3xl px-10 max-[480px]:px-4 pt-8">
              <div className="w-full">
                {/* Back to Homepage button */}
                <button
                  onClick={() => navigate("/")}
                  className="absolute top-8 left-8 flex items-center justify-center w-9 h-9 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-4 max-[480px]:space-y-3"
                >
                  {/* Header */}
                  <div className="mb-4 max-[480px]:mb-3 text-left mt-12">
                    <p className="!text-[22px] !font-bold !font-poppins text-blue uppercase tracking-wide mb-2 text-left">
                      ACCOUNT
                    </p>
                    <h1 className="text-orange mb-2 text-left !font-[Poppins] !text-[50px] !font-medium">
                      Sign Up
                    </h1>
                    <p className="text-[#1B3A5D] !font-montserrat !text-[18px] !font-semibold mb-2 text-left">
                      Sign Up for Free & Start Fundraising Today
                    </p>
                    <p className="!text-[12px] text-[#1B3A5D] !font-montserrat !font-bold text-left">
                      Already have an account?{" "}
                      <Link
                        to="/login"
                        state={{
                          back: `${state && state.back ? state.back : "/"}`,
                        }}
                        className="text-[#46A4DD] underline !font-bold"
                      >
                        Login
                      </Link>
                    </p>
                  </div>

                  {/* Form Fields */}
                  <div className="space-y-4 max-[540px]:space-y-3 max-[480px]:space-y-2">
                    {/* First Name and Last Name Row */}
                    <div className="grid grid-cols-2 gap-4 gap-x-6 max-[540px]:gap-x-4 max-[540px]:gap-2 max-[480px]:gap-x-2 max-[480px]:gap-1">
                      <div>
                        <label className={labelClassName}>
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          placeholder="First Name"
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label className={labelClassName}>
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          placeholder="Last Name"
                          className={inputClassName}
                        />
                      </div>
                    </div>

                    {/* Email and Phone Row */}
                    <div className="grid grid-cols-2 gap-4 gap-x-6 max-[540px]:gap-x-4 max-[540px]:gap-2 max-[480px]:gap-x-2 max-[480px]:gap-1">
                      <div>
                        <label className={labelClassName}>
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Email"
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label className={labelClassName}>Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Phone Number"
                          className={inputClassName}
                        />
                      </div>
                    </div>

                    {/* Password and Confirm Password Row */}
                    <div className="grid grid-cols-2 gap-4 gap-x-6 max-[540px]:gap-x-4 max-[540px]:gap-2 max-[480px]:gap-x-2 max-[480px]:gap-1">
                      <div>
                        <label className={labelClassName}>
                          Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          name="password"
                          required
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Password"
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label className={labelClassName}>
                          Confirm Password{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          required
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm Password"
                          className={inputClassName}
                        />
                      </div>
                    </div>

                    {/* Nonprofit Question */}
                    {!urlToken.get("token") && (
                      <div>
                        <label className={labelClassName}>
                          Are you registering as a nonprofit?{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="user_type"
                          value={formData.user_type}
                          onChange={handleChange}
                          required
                          className={selectClassName}
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: "right 12px center",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "16px",
                          }}
                        >
                          <option value="" disabled className="text-gray-400">
                            Select an Option
                          </option>
                          <option value="nonprofit_member">Yes</option>
                          <option value="regular_user">No</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Error Messages */}
                  {passwordError && (
                    <div className="text-red-500 text-sm mt-2 text-left">
                      {passwordError}
                    </div>
                  )}
                  {generalError && (
                    <div className="text-red-500 text-sm mt-2 text-left">
                      {generalError}
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="flex justify-center mt-6 max-[480px]:mt-4">
                    <SecondaryButton type="submit" className="px-10 py-3">
                      Sign Up
                    </SecondaryButton>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Image Section - Right Side (~45%) - Show on larger screens */}
          <div className="max-[1200px]:hidden w-[45%] h-screen p-6">
            <div className="w-full h-full">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/login-signup-pages/sign-up.png"
                alt="Cat and dog for signup page"
                className="object-cover h-full w-full rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-screen">
          <Spinner />
        </div>
      )}
    </>
  );
}

export default SignupPage;
